import type { GroupJoinPolicyResponse, UserJoinPolicyInput } from '../services/kieu';
import messages from '../locales/vi/joinCustomer.json';

export interface JoinPolicyFormProps {
  policyResponse: GroupJoinPolicyResponse | null;
  value: UserJoinPolicyInput;
  onChange: (value: UserJoinPolicyInput) => void;
  onResetDefaults: () => void;
  isDirty: boolean;
  loading?: boolean;
  disabled?: boolean;
  validationErrors?: Record<string, string>;
}

export function JoinPolicyForm({ policyResponse, value, onChange, onResetDefaults, loading, disabled, validationErrors = {} }: JoinPolicyFormProps) {
  const bounds = policyResponse?.bounds;
  const blocked = Boolean(policyResponse?.actorQuota?.isBlocked);
  const fields = [
    { key: 'delayMinMinutes' as const, label: messages.delayMin, min: Math.ceil((bounds?.minDelaySec ?? 180) / 60), max: Math.floor((bounds?.maxDelaySec ?? 600) / 60) },
    { key: 'delayMaxMinutes' as const, label: messages.delayMax, min: Math.ceil((bounds?.minDelaySec ?? 180) / 60), max: Math.floor((bounds?.maxDelaySec ?? 600) / 60) },
    { key: 'roundRestMinutes' as const, label: messages.rest, min: Math.ceil((bounds?.minRoundRestSec ?? 1800) / 60), max: Math.floor((bounds?.maxRoundRestSec ?? 86400) / 60) },
  ];
  return <div className="space-y-2 text-sm">
    <p className="text-xs text-slate-500">{messages.unlimited}</p>
    {blocked && <p role="status" className="rounded-lg bg-amber-50 p-3 text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">{messages.blocked}</p>}
    <details className="rounded-lg border border-slate-200 p-3 dark:border-slate-700">
      <summary className="cursor-pointer font-medium">{messages.settings}</summary>
      <p className="my-3 text-xs text-slate-500">{messages.automatic}</p>
      {loading ? <p>{messages.loading}</p> : <div className="grid gap-3 sm:grid-cols-3">
        {fields.map(field => <label key={field.key} className="space-y-1 text-xs">
          <span>{field.label}</span>
          <input aria-label={field.label} type="number" min={field.min} max={field.max} value={value[field.key]} disabled={disabled || blocked || !bounds}
            onChange={event => onChange({ ...value, [field.key]: event.target.value === '' ? '' : Number(event.target.value) })}
            className="block w-full rounded-lg border border-slate-200 bg-transparent px-3 py-2 dark:border-slate-600" />
          {validationErrors[field.key] && <span role="alert" className="text-red-600">{validationErrors[field.key]}</span>}
        </label>)}
      </div>}
      {validationErrors.delayRange && <p role="alert" className="mt-2 text-xs text-red-600">{validationErrors.delayRange}</p>}
      <button type="button" disabled={disabled || blocked || loading} onClick={onResetDefaults} className="mt-3 text-xs underline">{messages.reset}</button>
    </details>
  </div>;
}
